import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  People as PeopleIcon,
  ShoppingCart as CartIcon,
  Timeline as TimelineIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { getCurrentUser, hasPermission } from '../services/authService';

export default function LiveMonitoringDashboard() {
  const [liveData, setLiveData] = useState({
    activeUsers: [],
    recentEvents: [],
    cartActivity: [],
    performanceMetrics: {},
    errorLogs: [],
    sessionStats: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5000);
  const [filters] = useState({
    eventType: 'all',
    userType: 'all',
    timeRange: '1h'
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailDialog, setUserDetailDialog] = useState(false);
  const [exportDialog, setExportDialog] = useState(false);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [websocket, setWebsocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  
  const isAdmin = hasPermission('view_analytics');
  const refreshIntervalRef = useRef(null);

  const loadLiveData = useCallback(async () => {
    try {
      const response = await fetch('/api/live-monitoring?action=dashboard', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load live monitoring data');
      }
      
      const data = await response.json();
      setLiveData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error loading live data:', err);
    }
  }, []);

  const setupWebSocket = useCallback(() => {
    try {
      // Check if we're in an environment that might not support WebSockets
      const isLimitedEnvironment = window.location.hostname.includes('vercel.app') || 
                                  window.location.hostname.includes('netlify.app');
      
      if (isLimitedEnvironment) {
        console.log('Limited environment detected - using polling instead of WebSocket');
        setIsConnected(false);
        // Enable auto-refresh for limited environments
        setAutoRefresh(true);
        return;
      }
      
      // Use secure WebSocket for production (HTTPS) and regular WebSocket for development
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // Connect to the main WebSocket server (not a specific endpoint)
      const ws = new WebSocket(`${protocol}//${window.location.host}`);
      
      ws.onopen = () => {
        setIsConnected(true);
        console.log('Live monitoring WebSocket connected');
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleLiveUpdate(data);
      };
      
      ws.onclose = () => {
        setIsConnected(false);
        console.log('Live monitoring WebSocket disconnected');
        // Attempt to reconnect after 5 seconds
        setTimeout(() => setupWebSocket(), 5000);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
        // Fallback to polling on WebSocket error
        setAutoRefresh(true);
      };
      
      setWebsocket(ws);
    } catch (error) {
      console.warn('WebSocket not available, falling back to polling');
      // Fallback to polling if WebSocket fails
      setAutoRefresh(true);
    }
  }, []);

  const handleLiveUpdate = useCallback((data) => {
    setLiveData(prev => ({
      ...prev,
      ...data
    }));
  }, []);

  const setupAutoRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    
    // Use shorter refresh interval for limited environments (no WebSocket support)
    const isLimitedEnvironment = window.location.hostname.includes('vercel.app') || 
                                window.location.hostname.includes('netlify.app');
    const actualRefreshInterval = isLimitedEnvironment ? 5000 : refreshInterval; // 5 seconds for limited environments, normal interval for others
    
    refreshIntervalRef.current = setInterval(async () => {
      if (autoRefresh && !websocket) {
        await loadLiveData();
      }
    }, actualRefreshInterval);
  }, [autoRefresh, websocket, refreshInterval, loadLiveData]);

  const initializeLiveMonitoring = useCallback(async () => {
    try {
      setLoading(true);
      
      // Initial data load
      await loadLiveData();
      
      // Setup WebSocket for real-time updates
      setupWebSocket();
      
      // Setup auto-refresh
      if (autoRefresh) {
        setupAutoRefresh();
      }
      
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [loadLiveData, setupWebSocket, setupAutoRefresh, autoRefresh]);

  useEffect(() => {
    if (isAdmin) {
      initializeLiveMonitoring();
    }
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (websocket) {
        websocket.close();
      }
    };
  }, [isAdmin, initializeLiveMonitoring, websocket]);

  const handleRefresh = () => {
    loadLiveData();
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setUserDetailDialog(true);
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'cart_addition':
        return <CartIcon fontSize="small" />;
      case 'cable_completion':
        return <CheckCircleIcon fontSize="small" />;
      case 'error':
        return <ErrorIcon fontSize="small" />;
      case 'session_start':
        return <PersonIcon fontSize="small" />;
      case 'page_view':
        return <VisibilityIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  const getEventColor = (eventType) => {
    switch (eventType) {
      case 'cart_addition':
        return 'success';
      case 'cable_completion':
        return 'primary';
      case 'error':
        return 'error';
      case 'session_start':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStepName = (step) => {
    const stepNames = {
      0: 'Cable Type',
      1: 'Style',
      2: 'Connectors',
      3: 'End B',
      4: 'Wire Gauge/Length',
      5: 'Finishing',
      6: 'Review',
      7: 'Complete'
    };
    return stepNames[step] || `Step ${step}`;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getLocationFromIP = (ip) => {
    // This would typically call a geolocation service
    // For now, return a placeholder
    return ip === '127.0.0.1' ? 'Local' : 'Unknown';
  };

  const exportData = async (format) => {
    try {
      const response = await fetch('/api/analytics/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          format,
          filters,
          timeRange: filters.timeRange
        })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `live-monitoring-${new Date().toISOString().split('T')[0]}.${format}`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error">
          You don't have permission to access the live monitoring dashboard.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Live Monitoring Dashboard
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip 
              icon={isConnected ? <CheckCircleIcon /> : <WarningIcon />}
              label={isConnected ? 'Live Connected' : 'Polling Mode'}
              color={isConnected ? 'success' : 'warning'}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              Last updated: {new Date().toLocaleTimeString()}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Settings">
            <IconButton onClick={() => setSettingsDialog(true)}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Data">
            <IconButton onClick={() => setExportDialog(true)}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {liveData.activeUsers?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CartIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {liveData.cartActivity?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cart Additions (1h)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TimelineIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {liveData.recentEvents?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Events (1h)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ErrorIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {liveData.errorLogs?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Errors (1h)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Active Users */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active Users
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Step</TableCell>
                      <TableCell>IP</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {liveData.activeUsers?.map((user) => (
                      <TableRow key={user.sessionId} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                            <Typography variant="body2">
                              {user.userId.substring(0, 8)}...
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getStepName(user.currentStep)} 
                            size="small" 
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={getLocationFromIP(user.ip)}>
                            <Typography variant="body2">
                              {user.ip}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDuration(user.sessionDuration)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small" 
                            onClick={() => handleUserClick(user)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Cart Activity */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Cart Activity
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>User</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {liveData.cartActivity?.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTime(item.timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {item.sku}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="success.main">
                            ${typeof item.price === 'number' ? item.price.toFixed(2) : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.userId.substring(0, 8)}...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Live Activity Feed */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Live Activity Feed
              </Typography>
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {liveData.recentEvents?.map((event) => (
                  <ListItem key={event.id} divider>
                    <ListItemIcon>
                      {getEventIcon(event.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={event.type} 
                            size="small" 
                            color={getEventColor(event.type)}
                          />
                          <Typography variant="body2">
                            {event.data?.configType || event.data?.message || 'Event occurred'}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(event.timestamp)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            User: {event.userId.substring(0, 8)}...
                          </Typography>
                          {event.data?.price && typeof event.data.price === 'number' && (
                            <Typography variant="caption" color="success.main">
                              ${event.data.price.toFixed(2)}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User Detail Dialog */}
      <Dialog 
        open={userDetailDialog} 
        onClose={() => setUserDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          User Session Details
          <IconButton
            onClick={() => setUserDetailDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Session Info</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Session ID" 
                        secondary={selectedUser.sessionId}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="User ID" 
                        secondary={selectedUser.userId}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="IP Address" 
                        secondary={selectedUser.ip}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Current Step" 
                        secondary={getStepName(selectedUser.currentStep)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Session Duration" 
                        secondary={formatDuration(selectedUser.sessionDuration)}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>User Journey</Typography>
                  <List dense>
                    {selectedUser.journey?.map((step, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={step.action}
                          secondary={formatTime(step.timestamp)}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog 
        open={settingsDialog} 
        onClose={() => setSettingsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Monitoring Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch 
                  checked={autoRefresh} 
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
              }
              label="Auto Refresh"
            />
            
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Refresh Interval</InputLabel>
              <Select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(e.target.value)}
                disabled={!autoRefresh}
              >
                <MenuItem value={2000}>2 seconds</MenuItem>
                <MenuItem value={5000}>5 seconds</MenuItem>
                <MenuItem value={10000}>10 seconds</MenuItem>
                <MenuItem value={30000}>30 seconds</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog 
        open={exportDialog} 
        onClose={() => setExportDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Export Data</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Export Format</InputLabel>
              <Select
                value="json"
                onChange={(e) => exportData(e.target.value)}
              >
                <MenuItem value="json">JSON</MenuItem>
                <MenuItem value="csv">CSV</MenuItem>
                <MenuItem value="xlsx">Excel</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 